import { CheckOutlined, CloseOutlined } from "@ant-design/icons"
import {
  ChevronDownIcon,
  ChevronRightIcon,
  EllipsisHorizontalIcon,
} from "@heroicons/react/24/outline"
import { useMutation, useQueryClient } from "@tanstack/react-query"
import { Checkbox, Dropdown, Input, Progress, Switch, Tag, Tooltip } from "antd"
import { Button } from "components"
import { BusyOverlay, CustomSpin } from "components/custom/Loader"
import { formatDate } from "utils/formateDate"
import { newArchAxiosInstance } from "utils/request"
import { onQueryError } from "utils/utils"

export type KrRowType = "CG" | "DO" | "KR"

export const CollapseExpandButton = ({
  onClick,
  expanded,
}: {
  onClick: () => void
  expanded: boolean
}) => {
  return (
    <button
      type="button"
      onClick={onClick}
      className="w-8 h-8 rounded-full border border-[#E6ECF5] flex items-center justify-center"
    >
      {expanded ? (
        <ChevronDownIcon className="w-4 h-4 text-primary500" />
      ) : (
        <ChevronRightIcon className="w-4 h-4 text-primary500" />
      )}
    </button>
  )
}

const ImageMapping: Record<string, string> = {
  Individual:
    "https://padehrs3-staging.s3.us-east-2.amazonaws.com/images/c5228ad0-b631-452a-8ddf-c7ece442c6ef.svg",
  Department:
    "https://padehrs3-staging.s3.us-east-2.amazonaws.com/images/3ff8c694-cdf9-467e-a291-021d2ff075f7.svg",
  Company:
    "https://padehrs3-staging.s3.us-east-2.amazonaws.com/images/257cfbe4-a8a3-488f-a825-7a3febd7454f.svg",
}

type ObjType = "Individual" | "Department" | "Company"

export const CompActionsBtn = ({
  onView,
  onEdit,
  onOpenGoal,
  onCloseGoal,
  onDelete,
}: {
  onView?: () => void
  onEdit?: () => void
  onOpenGoal?: () => void
  onCloseGoal?: () => void
  onDelete?: () => void
}) => {
  const actionDefs: {
    key: string
    label: string
    onClick: () => void
    danger?: boolean
  }[] = [
    onView && { key: "view", label: "View", onClick: onView },
    onEdit && { key: "edit", label: "Edit", onClick: onEdit },
    onOpenGoal && { key: "open-goal", label: "Open Goal", onClick: onOpenGoal },
    onCloseGoal && {
      key: "close-goal",
      label: "Close Goal",
      onClick: onCloseGoal,
    },
    onDelete && {
      key: "delete",
      label: "Delete",
      onClick: onDelete,
      danger: true,
    },
  ].filter(Boolean) as {
    key: string
    label: string
    onClick: () => void
    danger?: boolean
  }[]

  const items = actionDefs.map(({ key, label, onClick, danger }) => ({
    key,
    label: (
      <button
        className={`font-circular w-full text-left ${
          danger ? "text-danger400" : "text-neutral400"
        }`}
        onClick={onClick}
      >
        {label}
      </button>
    ),
  }))

  if (!items.length) return null

  return (
    <Dropdown
      menu={{
        items,
      }}
      // trigger={[""]}
    >
      <button>
        <EllipsisHorizontalIcon className="w-6 h-6 text-primary500" />
      </button>
    </Dropdown>
  )
}

export const ObjTypeImg = ({ objType }: { objType: ObjType }) => {
  const src = ImageMapping[objType] || ImageMapping["Individual"] // Default to Individual image if objType is not found
  return (
    <img
      src={src}
      alt={objType}
    />
  )
}

export const ObjTypeTag = ({
  title,
  objType,
}: {
  title: string
  objType: ObjType
}) => {
  return (
    <span
      className={`${
        objType === "Individual"
          ? "text-[#1F703F] bg-[#C1EFC8]"
          : objType === "Department"
          ? "text-[#261F70] bg-[#C8C1EF]"
          : "text-[#6C1F70] bg-[#EFC1E0]"
      } font-circular font-medium px-3 py-1.5 rounded-full text-xs shadow`}
    >
      {title}
    </span>
  )
}

export const ObjTitle = ({ title }: { title: string }) => {
  return (
    <div className="flex gap-3 items-center">
      <img
        className="object-cover h-4 w-4"
        src={
          "https://padehrs3-staging.s3.us-east-2.amazonaws.com/images/3ff8c694-cdf9-467e-a291-021d2ff075f7.svg"
        }
        alt={""}
        width={15}
        height={15}
      />

      <h4 className="text-padeBlack font-circular font-semibold text-sm flex-wrap">
        {title}
      </h4>
    </div>
  )
}

export const ObjStatusTag = ({
  status,
  statusId,
}: {
  status: string
  statusId: number
}) => {
  return (
    <Tag
      color={
        statusId === 2 ? "error" : statusId === 1 ? "default" : "processing"
      }
    >
      <span
        className={`${
          statusId === 2
            ? "text-[#D32F2F]"
            : statusId === 1
            ? "text-[#6C6C6C]"
            : "text-[#0077B6]"
        } font-circular font-medium`}
      >
        {status}
      </span>
    </Tag>
  )
}

export const ObjCreatedDate = ({ date }: { date: string }) => {
  return (
    <span className="text-padeSubtext text-xs">
      Created: <span className="font-semibold">{formatDate(date)}</span>
    </span>
  )
}

export const ObjCountTag = ({ objCount }: { objCount: number }) => {
  return (
    <span className="px-3 py-1.5 rounded-full text-xs bg-[#F1F5F9] text-[#0C1B3D] font-circular shadow-sm">
      {objCount} objective
      {objCount !== 1 ? "s" : ""}
    </span>
  )
}

export const ObjKRCountTag = ({
  krCount,
  noun = "key result",
}: {
  krCount: number
  /** Balanced Scorecard calls these KPIs, not key results. */
  noun?: string
}) => {
  return (
    <span className="px-3 py-1.5 rounded-full text-xs bg-[#F1F5F9] text-[#0C1B3D] font-circular shadow-sm">
      {krCount} {noun}
      {krCount !== 1 ? "s" : ""}
    </span>
  )
}

export const ObjProgress = ({ progress }: { progress: number }) => {
  return (
    <div>
      <div className="mb-1 flex justify-between items-center">
        <span className="text-padeBlack text-sm font-semibold font-circular">
          Progress
        </span>
        <span className="text-padeBlack text-sm font-semibold font-circular">
          {progress}%
        </span>
      </div>
      <Progress
        percent={progress}
        showInfo={false}
        strokeColor={
          progress < 20 ? "#F41418" : progress < 50 ? "#F5A623" : "#13B378"
        }
        trailColor="#EEF0F7"
      />
    </div>
  )
}

export const ObjActionsBtn = ({
  onView,
  onEdit,
  onAddKR,
  onDelete,
}: {
  onView?: () => void
  onEdit?: () => void
  onAddKR?: () => void
  onDelete?: () => void
}) => {
  const actionDefs: {
    key: string
    label: string
    onClick: () => void
    danger?: boolean
  }[] = [
    onView && { key: "view", label: "View", onClick: onView },
    onEdit && { key: "edit", label: "Edit", onClick: onEdit },
    onAddKR && { key: "addKR", label: "Add Key Result", onClick: onAddKR },
    onDelete && {
      key: "delete",
      label: "Delete",
      onClick: onDelete,
      danger: true,
    },
  ].filter(Boolean) as {
    key: string
    label: string
    onClick: () => void
    danger?: boolean
  }[]

  const items = actionDefs.map(({ key, label, onClick, danger }) => ({
    key,
    label: (
      <button
        className={`font-circular w-full text-left ${
          danger ? "text-danger400" : "text-neutral400"
        }`}
        onClick={onClick}
      >
        {label}
      </button>
    ),
  }))

  if (!items.length) return null

  return (
    <Dropdown
      menu={{
        items,
      }}
      // trigger={[""]}
    >
      <button>
        <EllipsisHorizontalIcon className="w-6 h-6 text-primary500" />
      </button>
    </Dropdown>
  )
}

export const ObjEmptyState = ({
  text,
  btnText,
  onCreate,
  showBtn = true,
}: {
  text: string
  btnText?: string
  onCreate?: () => void
  showBtn?: boolean
}) => {
  return (
    <div className=" bg-white flex flex-col justify-center items-center gap-4 py-6">
      <img
        src="https://padehrs3-staging.s3.us-east-2.amazonaws.com/images/18dbd40c-8c22-4a6c-9ffb-967b21aff674.svg"
        alt=""
      />
      <h6 className="text-base font-avenir text-padeBlack">
        {text ?? " Set your first objective to get started."}
      </h6>

      {showBtn ? (
        <Button
          color="primary"
          onClick={onCreate}
        >
          {btnText ?? "Create Objective"}
        </Button>
      ) : null}
    </div>
  )
}

export const ObjAddButton = ({
  onClick,
  className,
}: {
  onClick: () => void
  className?: string
}) => {
  return (
    <div className="f">
      <button
        type="button"
        onClick={onClick}
        className={`font-circular text-primary500 text-sm font-semibold ${
          className ?? ""
        }`}
      >
        + Add Department Objective
      </button>
    </div>
  )
}

// #region KEY RESULT COMPONENTS

export const KRsWrapper = ({
  children,
  header = "Key Results",
  hideHeader = false,
}: {
  children: React.ReactNode
  hideHeader?: boolean
  header?: React.ReactNode
}) => {
  return (
    <div className="bg-[#F3F5FB99] rounded-xl p-4 flex flex-col gap-4 shadow-sm">
      {!hideHeader && (
        <span className="text-padeBlack font-semibold font-circular text-sm">
          {header}
        </span>
      )}

      {children}
    </div>
  )
}

export const KRAddButton = ({
  onClick,
  className,
  label = "Add Key Result",
}: {
  onClick: () => void
  className?: string
  /** Overrides the default wording — an employee proposes a KPI rather than adding one outright. */
  label?: string
}) => {
  return (
    <div className="f">
      <button
        type="button"
        onClick={onClick}
        className={`font-circular text-primary500 text-sm font-semibold ${
          className ?? ""
        }`}
      >
        + {label}
      </button>
    </div>
  )
}

export const KRItemWrapper = ({
  children,
  loading,
}: {
  children: React.ReactNode
  loading: boolean
}) => {
  // An overlay, not a loading region: the row is already on screen while its save is in flight, and a
  // region unmounts its children — the actual input then remounted showing the figure from before the save.
  return (
    <BusyOverlay spinning={loading}>
      <div className="bg-white shadow rounded-md py-2 px-2.5 grid grid-cols-1 xl:grid-cols-[minmax(0,1fr)_auto] items-center gap-4 xl:gap-6">
        {children}
      </div>
    </BusyOverlay>
  )
}

export const KRItemTitle = ({
  title,
  onClick,
  type = "KR",
  className,
}: {
  type?: KrRowType
  title: string
  onClick?: () => void
  className?: string
}) => {
  return (
    <button
      className={`flex min-w-[300px] items-center gap-2 cursor-pointer ${className ?? ""}`}
      type="button"
      onClick={onClick}
    >
      <Tooltip
        title={
          type === "CG"
            ? "Company Goal"
            : type === "DO"
            ? "Department Objective"
            : "Employee Key Result"
        }
      >
        <div
          className={`h-6 w-6 font-semibold font-circular flex justify-center items-center text-[10px] rounded-full shrink-0 ${
            type === "CG"
              ? "bg-[#e7f3e8] text-[#049010]"
              : type === "DO"
              ? "bg-[#f6eef6] text-[#6C1F70]"
              : "bg-primary50 text-primary500"
          }`}
        >
          {type}
        </div>
      </Tooltip>
      <Tooltip title={title}>
        <span className="text-sm text-start font-circular text-padeBlack ">
          {title}
        </span>
      </Tooltip>
    </button>
  )
}

export const KRSwitch = ({
  score,
  onChange,
  loading,
  disabled,
  role = "employee",
}: {
  score: number
  onChange?: (checked: boolean) => void
  loading?: boolean
  disabled?: boolean
  role?: "employee" | "manager"
}) => {
  return (
    <Tooltip
      title={role === "manager" ? "Manager's score" : "Employee's score"}
    >
      <div className="px-2 py-1 rounded flex justify-center gap-2 w-[88px] items-center">
        <Switch
          checkedChildren={<CheckOutlined />}
          unCheckedChildren={<CloseOutlined />}
          rootClassName={`${
            score === 100
              ? role === "manager"
                ? "!bg-[#38CB89] border-[#38CB89]"
                : "!bg-primary500 border-[#38CB89]"
              : "!bg-[#ededed] border-[#E69702]"
          } relative z-[1]`}
          defaultChecked={score === 100}
          onChange={onChange}
          disabled={loading || disabled}
          loading={loading}
        />
      </div>
    </Tooltip>
  )
}

type KRInputProps = {
  viewType: "view" | "edit"
  metric: number
  targetValue: number | string
  metricCurrency?: string
  defaultScore?: number
  onBlur: (value: number) => void
  disabled?: boolean
}
export const KRInput = ({
  viewType,
  metric,
  targetValue,
  metricCurrency = "",
  defaultScore,
  onBlur,
  disabled,
}: KRInputProps) => {
  return (
    <div
      className={`border ${
        viewType === "view" ? "border-[#B37FEB]" : "border-[#0C1B3D1A]"
      } px-2 py-1 rounded flex justify-center gap-2 items-center [&_.ant-input-disabled]:!text-padeSubtext`}
    >
      <Input
        prefix={metric === 2 ? metricCurrency : undefined}
        suffix={metric === 0 ? "%" : undefined}
        className={
          "!p-0 !text-padeBlack !font-circular disabled:!text-padeBlack font-medium text-xs !border-none text-center min-w-[40px] max-w-[80px] !bg-transparent"
        }
        defaultValue={defaultScore ?? undefined}
        placeholder="Enter score"
        onBlur={(e) => {
          const value = Number(e.target.value)
          if (value === defaultScore) return
          if (metric === 0 && value > 100) {
            e.target.value = "100"
            onBlur(100)
            return
          }
          onBlur(value)
        }}
        disabled={disabled || viewType === "view"}
      />
      <p>|</p>
      <p className="text-padeBlack font-medium text-xs font-circular shrink-0">
        {metric === 2 ? metricCurrency : ""}
        {targetValue}
        {metric === 0 ? "%" : ""}
      </p>
    </div>
  )
}

export const KRDueDate = ({ date }: { date: string }) => {
  return (
    <span className="text-padeSubtext text-xs font-avenir w-[110px]">
      Due: {formatDate(date)}{" "}
    </span>
  )
}

export const KRWeight = ({
  weight,
  isWeightAssigned,
  tooltip,
  valueLabel,
}: {
  weight: number | undefined
  isWeightAssigned: boolean
  /** Replaces the OKR wording, for frameworks whose weight is a share of something other than an objective. */
  tooltip?: string
  /** Replaces the percentage, for a weight with no single figure to show. */
  valueLabel?: string
}) => {
  const weightLabel = isWeightAssigned
    ? weight === 0
      ? "Zero"
      : "Fixed"
    : "Auto"

  const weightTooltip =
    tooltip ??
    (isWeightAssigned
      ? weight === 0
        ? "This Key Result is being tracked but does not contribute to the Objective's overall progress."
        : `This Key Result has a fixed weight of ${weight}%. It contributes ${weight}% to the Objective's overall progress.`
      : "The Auto-Weight Key Result shares the remaining weight equally with other unweighted key results.")

  return (
    <Tooltip title={weightTooltip}>
      <div className="flex items-center gap-1">
        <div className="flex flex-col items-center">
          <span className="text-padeSubtext text-[10px] !leading-none font-avenir">
            {weightLabel}
          </span>

          <img
            src="https://padehrs3-staging.s3.us-east-2.amazonaws.com/images/c5b9012a-4566-487f-ab65-43c5a4a5f4a3.svg"
            alt=""
            className="rotate-45 w-4"
          />
        </div>
        <p className="text-padeSubtext font-circular text-xs font-semibold">
          {valueLabel ?? `${weight || 0}%`}
        </p>
      </div>
    </Tooltip>
  )
}

export const KRProgress = ({ progress }: { progress: number }) => {
  return (
    <div className="flex gap-1 items-center">
      <Progress
        percent={progress || 0}
        strokeColor={{
          "0%": "#94B8FA",
          "100%": "#94B8FA",
        }}
        showInfo={false}
        className="w-[120px] mb-0"
      />

      <p className="text-padeBlack text-xs font-circular font-semibold">
        {progress || 0}%
      </p>
    </div>
  )
}

export const KRActionsBtn = ({
  onView,
  onEdit,
  onDelete,
  onMarkComplete,
  onAddInitiative,
}: {
  onView?: () => void
  onEdit?: () => void
  onDelete?: () => void
  onMarkComplete?: () => void
  onAddInitiative?: () => void
}) => {
  const actionDefs: {
    key: string
    label: string
    onClick: () => void
    danger?: boolean
  }[] = [
    onView && { key: "view", label: "View", onClick: onView },
    onEdit && { key: "edit", label: "Edit", onClick: onEdit },
    onMarkComplete && {
      key: "mark-complete",
      label: "Mark as Complete",
      onClick: onMarkComplete,
    },
    onAddInitiative && {
      key: "add-initiative",
      label: "Add Initiative",
      onClick: onAddInitiative,
    },
    onDelete && {
      key: "delete",
      label: "Delete",
      onClick: onDelete,
      danger: true,
    },
  ].filter(Boolean) as {
    key: string
    label: string
    onClick: () => void
    danger?: boolean
  }[]

  const items = actionDefs.map(({ key, label, onClick, danger }) => ({
    key,
    label: (
      <button
        className={`font-circular w-full text-left ${
          danger ? "text-danger400" : "text-neutral400"
        }`}
        onClick={onClick}
      >
        {label}
      </button>
    ),
  }))

  if (!items.length) return null

  return (
    <Dropdown
      menu={{
        items,
      }}
      // trigger={["click"]}
    >
      <button>
        <EllipsisHorizontalIcon className="w-6 h-6 text-primary500" />
      </button>
    </Dropdown>
  )
}

// #endregion

// #region INITIATIVE COMPONENTS

export type IInitiativesListing = IBaseRes<IInitiativesList[]>
export interface IInitiativesList {
  initiativeId: number
  initiativeKey: string
  keyResultId: number
  keyResultName: string
  initiativeName: string
  isCompleted: boolean
  timeLine: string
}
export const InitiativeRow = ({
  initiative,
  onChangeSuccess,
}: {
  initiative: IInitiativesList
  onChangeSuccess?: () => void
}) => {
  const { isLoading: isSubmitting, mutate: submit } = useMutation({
    mutationFn: async () => {
      const endpoint = `/api/v2/employee/okr/initiatives/${initiative.initiativeKey}`
      const res = await newArchAxiosInstance.post(endpoint)
      return res.data
    },
    onSuccess: onChangeSuccess,
    onError: onQueryError,
  })

  // /api/v{version}/employee/okr/initiatives/{initiativeKey}
  return (
    <BusyOverlay spinning={isSubmitting}>
      <div className="py-2.5 px-2 shadow-sm  bg-white  rounded-md flex items-center justify-between ">
        <div className="flex items-center gap-2">
          <Checkbox
            style={{
              fontSize: "20px",
            }}
            className="round-checkbox"
            checked={initiative.isCompleted}
            onChange={(e) => {
              submit()
            }}
            disabled={isSubmitting}
          />
          <p className="text-[#42526D] font-medium text-sm ">
            {initiative.initiativeName}
          </p>
        </div>
      </div>
    </BusyOverlay>
  )
}

export const Initiatives = ({
  loading,
  initiatives,
  onChangeSuccess,
}: {
  loading: boolean
  initiatives: IInitiativesListing
  krId: number
  role: "employee" | "manager"
  onChangeSuccess?: () => void
}) => {
  return (
    <div className="flex flex-col gap-2 pl-6 mt-2 ">
      {loading ? (
        <div className="my-10 flex w-full justify-center items-center ">
          <CustomSpin spinning />
        </div>
      ) : initiatives?.data?.length ? (
        <>
          {initiatives?.data?.map((initiative) => (
            <InitiativeRow
              key={`initiative-${initiative.initiativeId}`}
              initiative={initiative}
              onChangeSuccess={onChangeSuccess}
            />
          ))}
        </>
      ) : (
        <div className="my-10 flex w-full justify-center items-center ">
          <p className="text-sm text-padeSubtext">No initiatives found.</p>
        </div>
      )}
    </div>
  )
}

// #endregion

export const OBJ = {
  CollapseExpandButton,
  ObjTypeImg,
  ObjTypeTag,
  ObjTitle,
  ObjStatusTag,
  ObjCreatedDate,
  ObjCountTag,
  ObjKRCountTag,
  ObjProgress,
  ObjEmptyState,
  ObjActionsBtn,
  ObjAddButton,
}

export const KR = {
  KRInput,
  KRDueDate,
  KRWeight,
  KRProgress,
  KRSwitch,
  KRItemTitle,
  KRsWrapper,
  KRAddButton,
  KRItemWrapper,
  KRActionsBtn,
}

export const Initiative = {
  InitiativeRow,
  Initiatives,
}
